/** @jsxImportSource @emotion/react */

import { DropdownMenuItem, Icon } from "@a_team/ui-components";
import { AccountCollaboratorRemoveModal } from "components/AccountCollaboratoRemoveModal";
import { useDropdown } from "components/DropdownPortal";
import { MissionCollaboratorRemoveModal } from "components/MissionCollaboratorRemoveModal";
import { Flags } from "configs/featureFlags";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { observer } from "mobx-react";
import { AccountMember } from "models/Account";
import { useResendCollaboratorInvite } from "queries/accounts/collaborators/useResendCollaboratorInvite";
import { useMemo, useState } from "react";
import { useRootStore } from "store";

interface CollaboratorActionProps {
  collaborator: AccountMember;
}

export const CollaboratorAction = (props: CollaboratorActionProps) => {
  const {
    accountsStore: { currentAccountId },
    missionSpecStore: { missionSpec },
  } = useRootStore();
  const { collaborator } = props;
  const { toggleDropdown, isOpen, Dropdown } = useDropdown();
  const [collaboratorSelectedForRemoval, setCollaboratorSelectedForRemoval] =
    useState<AccountMember["user"]>();

  const { flagEnabled: clientRolesAndPermsEnabled } = useFeatureFlag(
    Flags.ClientRolesAndPerms
  );

  const { mutateAsync: resendCollaboratorInvite } =
    useResendCollaboratorInvite();

  const isMissionInvite = useMemo(() => !!missionSpec, [missionSpec]);
  const { user } = collaborator;
  if (!user) {
    return null;
  }

  const acceptedInvite = user.activated;
  const userId = user.id;

  return (
    <div data-testing-id={`collaborator-options-toggle-${userId}`}>
      <Icon name="more" onClick={toggleDropdown} />
      {isOpen && (
        <Dropdown>
          {!acceptedInvite && (
            <DropdownMenuItem
              onClick={() => {
                if (!currentAccountId || !userId) {
                  return;
                }
                toggleDropdown();
                resendCollaboratorInvite({
                  accountId: currentAccountId,
                  userId,
                });
              }}
            >
              Resend invite
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onClick={() => {
              toggleDropdown();
              setCollaboratorSelectedForRemoval(collaborator.user);
            }}
            danger={true}
          >
            <span data-testing-id={`remove-${userId}`}>Remove</span>
          </DropdownMenuItem>
        </Dropdown>
      )}
      {clientRolesAndPermsEnabled && isMissionInvite ? (
        <MissionCollaboratorRemoveModal
          missionId={missionSpec?.id}
          name={collaboratorSelectedForRemoval?.fullName || "your collaborator"}
          isOpen={!!collaboratorSelectedForRemoval}
          onCancel={() => setCollaboratorSelectedForRemoval(undefined)}
          userId={collaboratorSelectedForRemoval?.id}
        />
      ) : (
        <AccountCollaboratorRemoveModal
          firstName={
            collaboratorSelectedForRemoval?.firstName || "your collaborator"
          }
          isOpen={!!collaboratorSelectedForRemoval}
          onCancel={() => setCollaboratorSelectedForRemoval(undefined)}
          userId={collaboratorSelectedForRemoval?.id}
        />
      )}
    </div>
  );
};

export default observer(CollaboratorAction);
